import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, parse } from "date-fns";
import { toast } from "sonner";
// Praying Hands Icon Component - Two hands pressed together
const PrayingHandsIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 36 36" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Based on classic praying hands emoji design */}
    <path d="M18 2L15 5V14L13 16V20L10 23V28L14 32H18L22 32L26 28V23L23 20V16L21 14V5L18 2Z"/>
    <path d="M15 5C14 5 13 6 13 7V12C13 12 12 13 12 14V18C12 18 10 20 10 21V26C10 27 11 28 12 28H18" fill="currentColor" opacity="0.7"/>
    <path d="M21 5C22 5 23 6 23 7V12C23 12 24 13 24 14V18C24 18 26 20 26 21V26C26 27 25 28 24 28H18" fill="currentColor" opacity="0.7"/>
  </svg>
);
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate 96 time slots (15 min each)
const generateTimeSlots = () => {
  const slots = [];
  for (let i = 0; i < 96; i++) {
    const hours = Math.floor(i / 4);
    const minutes = (i % 4) * 15;
    const period = hours < 12 ? "AM" : "PM";
    let displayHours = hours % 12;
    if (displayHours === 0) displayHours = 12;
    slots.push({
      index: i,
      label: `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`,
    });
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const PrayerWall = () => {
  const [selectedDate, setSelectedDate] = useState(new Date(2026, 3, 14)); // April 14, 2026
  const [claimedSlots, setClaimedSlots] = useState({});
  const [stats, setStats] = useState({ claimed_slots: 0, hours_remaining: 24 });
  const [filter, setFilter] = useState("all");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [intention, setIntention] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const dateString = format(selectedDate, "yyyy-MM-dd");
  const displayDate = format(selectedDate, "EEEE, MMMM d, yyyy");

  const fetchSlots = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/slots?date=${dateString}`);
      const slotsMap = {};
      response.data.forEach((slot) => {
        slotsMap[slot.time_slot] = slot;
      });
      setClaimedSlots(slotsMap);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  }, [dateString]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/slots/stats?date=${dateString}`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [dateString]);

  useEffect(() => {
    fetchSlots();
    fetchStats();
    
    // Poll for updates every 3 seconds
    const interval = setInterval(() => {
      fetchSlots();
      fetchStats();
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchSlots, fetchStats]);

  const handleSlotClick = (slot) => {
    if (claimedSlots[slot.index]) return;
    setSelectedSlot(slot);
    setName("");
    setIntention("");
    setModalOpen(true);
  };

  const handleClaim = async () => {
    if (!name.trim()) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/slots/claim`, {
        date: dateString,
        time_slot: selectedSlot.index,
        name: name.trim(),
        intention: intention.trim() || null,
      });
      
      setModalOpen(false);
      toast.success(
        `Thank you, ${name.trim()}! You've committed to pray at ${selectedSlot.label}.`,
        { duration: 6000 }
      );
      
      fetchSlots();
      fetchStats();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("This slot was just claimed by someone else. Please choose another.");
      } else {
        toast.error("Failed to claim slot. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredSlots = TIME_SLOTS.filter((slot) => {
    if (filter === "open") return !claimedSlots[slot.index];
    if (filter === "claimed") return !!claimedSlots[slot.index];
    return true;
  });

  const progressPercent = (stats.claimed_slots / 96) * 100;
  const hoursRemaining = stats.hours_remaining;

  return (
    <div className="min-h-screen bg-pw-bg">
      {/* Header */}
      <header className="bg-pw-header px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_prayer-wall-1/artifacts/ipu8shv3_players-health-lockup-color%20copy%20%284%29.png" 
              alt="Players Health" 
              className="h-10 w-auto"
            />
            <div className="text-xs tracking-[0.2em] text-pw-purple uppercase">
              24-HOUR PRAYER WALL
            </div>
          </div>

          {/* Right side - Date picker and Stats */}
          <div className="flex items-center gap-6 flex-wrap">
            {/* Date Picker */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 uppercase tracking-wider">Date:</span>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-testid="date-picker-input"
                    className="bg-pw-slot border border-pw-border rounded px-3 py-1.5 text-sm text-white hover:border-pw-purple transition-colors"
                  >
                    {format(selectedDate, "MMM d, yyyy")}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-pw-header border-pw-border" align="end">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(date);
                        setCalendarOpen(false);
                      }
                    }}
                    initialFocus
                    className="bg-pw-header text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Stats */}
            <div data-testid="claimed-stats-counter" className="flex items-center gap-2">
              <span className="text-2xl font-bold text-pw-teal">{stats.claimed_slots}</span>
              <span className="text-xs text-gray-400 uppercase tracking-wider">/ 96 Claimed</span>
            </div>
          </div>
        </div>
      </header>

      {/* Warm gradient bar */}
      <div className="h-[3px] gradient-warm" />

      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
        <div className="max-w-3xl mx-auto">
          {/* Badge */}
          <div className="inline-block mb-4">
            <span className="px-4 py-1.5 rounded-full text-xs tracking-[0.2em] uppercase bg-pw-border text-white border border-[#333]">
              A SEASON OF ADVERSITY
            </span>
          </div>

          {/* Date Banner */}
          <div className="text-pw-teal text-sm sm:text-base tracking-[0.15em] uppercase font-heading mb-6">
            {displayDate}
          </div>

          {/* Main Heading */}
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight leading-tight mb-6">
            STAND IN THE GAP FOR PLAYERS HEALTH
          </h1>

          {/* Body Text */}
          <p className="font-body text-pw-muted text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            We are walking through a challenging season and we are inviting our community 
            to lift us up in prayer. Select a 15-minute window and commit to interceding 
            on behalf of our team, mission, and the athletes we serve.
          </p>

          {/* Scripture */}
          <blockquote className="border-l-2 border-pw-teal pl-4 sm:pl-6 text-left max-w-xl mx-auto">
            <p className="font-body italic text-pw-teal text-sm sm:text-base leading-relaxed mb-2">
              "Do not be anxious about anything, but in every situation, by prayer and petition, 
              with thanksgiving, present your requests to God. And the peace of God, which 
              transcends all understanding, will guard your hearts and your minds in Christ Jesus."
            </p>
            <cite className="text-xs tracking-[0.2em] uppercase text-pw-purple not-italic">
              — Philippians 4:6-7
            </cite>
          </blockquote>
        </div>
      </section>

      {/* Progress Section */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Header */}
          <div className="flex justify-between items-center mb-3">
            <span className="text-xs text-gray-400 uppercase tracking-wider">Prayer Coverage</span>
            <span className="text-sm text-pw-teal font-bold">{Math.round(progressPercent)}%</span>
          </div>

          {/* Progress Bar */}
          <div 
            data-testid="prayer-progress-bar"
            className="h-2 bg-pw-border rounded-full overflow-hidden mb-3"
          >
            <div 
              className="h-full gradient-cool transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Progress Message */}
          <p className="text-center text-sm text-gray-400">
            {hoursRemaining > 0 ? (
              <>
                <span className="text-pw-teal font-bold">{hoursRemaining.toFixed(1)} hours</span> still need a prayer warrior
              </>
            ) : (
              <span className="text-pw-teal font-bold">All 24 hours are covered — Amen!</span>
            )}
          </p>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex justify-center gap-2">
          {["all", "open", "claimed"].map((f) => (
            <button
              key={f}
              data-testid={`filter-btn-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded text-xs uppercase tracking-wider transition-colors ${
                filter === f
                  ? "bg-pw-purple text-white"
                  : "bg-pw-border text-pw-muted hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* Time Slot Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          {filteredSlots.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No {filter} slots for this date.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-3">
              {filteredSlots.map((slot, index) => {
                const claimed = claimedSlots[slot.index];
                return (
                  <div
                    key={slot.index}
                    data-testid={claimed ? "time-slot-claimed" : "time-slot-open"}
                    onClick={() => handleSlotClick(slot)}
                    className={`
                      min-h-[100px] p-3 rounded-lg flex flex-col items-center justify-center text-center
                      transition-all duration-200 animate-fade-in
                      ${claimed 
                        ? "slot-claimed-bg border border-pw-teal/40 cursor-not-allowed" 
                        : "bg-pw-slot border border-pw-border hover:border-pw-purple/60 cursor-pointer"
                      }
                    `}
                    style={{ animationDelay: `${index * 10}ms` }}
                  >
                    {/* Time Label */}
                    <span className="font-mono text-xs text-pw-muted mb-2">
                      {slot.label}
                    </span>

                    {claimed ? (
                      <>
                        {/* Praying Hands Emoji */}
                        <span className="text-xl mb-1">🙏</span>
                        {/* Name */}
                        <span className="text-xs font-bold text-pw-teal truncate w-full">
                          {claimed.name}
                        </span>
                        {/* Intention */}
                        {claimed.intention && (
                          <span className="text-[10px] text-pw-muted italic line-clamp-2 w-full mt-1 leading-tight">
                            "{claimed.intention}"
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-500 uppercase tracking-wider">
                        Open
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto">
        <div className="h-[3px] gradient-warm" />
        <div className="bg-pw-header px-4 py-6 text-center">
          <p className="text-xs text-gray-500 tracking-wider">
            PLAYERSHEALTH.COM · "The peace of God, which transcends all understanding, will guard your hearts." — Phil. 4:7
          </p>
        </div>
      </footer>

      {/* Claim Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent 
          data-testid="claim-slot-modal"
          className="bg-pw-header border-pw-border max-w-md"
        >
          {/* Gradient Top Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 gradient-cool rounded-t-lg" />
          
          <DialogHeader className="pt-4">
            <div className="flex justify-center mb-2">
              <span className="px-3 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase bg-pw-border text-pw-purple border border-[#333]">
                COMMIT TO PRAY
              </span>
            </div>
            <DialogTitle className="font-heading text-xl text-white text-center uppercase tracking-tight">
              {selectedSlot?.label}
            </DialogTitle>
            <DialogDescription className="text-center text-sm text-pw-muted">
              {displayDate}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {/* Encouraging Message */}
            <div className="bg-pw-slot border border-pw-border rounded-lg p-4 text-center">
              <p className="text-pw-teal text-sm font-body italic">
                "Thank you for standing in the gap! Your prayers make a difference."
              </p>
            </div>
            {/* Name Input */}
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                Your Name *
              </label>
              <input
                data-testid="claim-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="w-full bg-[#141414] border border-pw-border rounded px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-pw-purple transition-colors"
              />
            </div>

            {/* Intention Input */}
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                Prayer Intention or Encouragement (Optional)
              </label>
              <textarea
                data-testid="claim-intention-input"
                value={intention}
                onChange={(e) => setIntention(e.target.value)}
                placeholder="Share a word of encouragement, a scripture, or what you'll be praying for..."
                maxLength={120}
                rows={3}
                className="w-full bg-[#141414] border border-pw-border rounded px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-pw-purple transition-colors resize-none"
              />
              <div className="text-right text-[10px] text-gray-500 mt-1">
                {intention.length}/120
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setModalOpen(false)}
                className="flex-1 px-4 py-3 rounded bg-pw-border text-pw-muted uppercase text-xs tracking-wider hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="claim-submit-button"
                onClick={handleClaim}
                disabled={!name.trim() || isSubmitting}
                className="flex-1 px-4 py-3 rounded gradient-cool text-white font-bold uppercase text-xs tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Claiming..." : "Claim Slot"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrayerWall;
